/**
 * The editor's canvas and inspector side by side, with a handle between them
 * that sets the inspector's width: dragged, or with the arrow keys on it, and
 * back to the default on a double-click. The inspector is never narrower
 * than the default, and the canvas keeps at least half the row. The width is
 * a per-viewer convenience kept in localStorage.
 *
 * Opening a gate or a step (a new `fitKey`) widens the inspector to show its
 * longest field whole, up to the same cap; picking a longer field fits again.
 * Only the viewer's own width is kept: the fit is never stored, never makes
 * the inspector narrower than that width, and gives way to the handle until
 * the next opening. With no gate or step open, the width stays as it is.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

const STORAGE_KEY = 'optum.linkages.inspector-width';
/** The inspector's width before anyone drags it: the editor's layout before the handle. */
export const INSPECTOR_DEFAULT = 320;
const HANDLE = 16;
const KEY_STEP = 16;
// A field's text, as a picker marks it, that wants its whole width.
const FIT_TEXT = '[data-fit-text]';
// Room past a text's own width, so subpixel rounding never clips its last letter.
const FIT_SLACK = 2;
// A fit that moves by less than this is the same fit, so measuring settles.
const FIT_SAME = 3;
const EASE_MS = 150;

/** What the inspector must be to show one text whole: its width plus what sits around its box. */
export interface FitText {
  text: number;
  box: number;
}

/** The widest the inspector may be in a row this wide: the canvas keeps half of what the handle leaves. */
export function widest(row: number): number {
  return Math.max(INSPECTOR_DEFAULT, Math.floor((row - HANDLE) / 2));
}

/** The inspector's width kept inside its bounds for a row this wide. */
export function clampWidth(width: number, row: number): number {
  if (!Number.isFinite(width)) return INSPECTOR_DEFAULT;
  return Math.min(widest(row), Math.max(INSPECTOR_DEFAULT, Math.round(width)));
}

/** The inspector width that shows every marked text whole, from widths read at one moment. */
export function fittedWidth(inspector: number, texts: readonly FitText[]): number {
  return Math.max(0, ...texts.map((t) => Math.ceil(t.text + inspector - t.box + FIT_SLACK)));
}

/** Read the inspector's marked texts: each text's own width and its box's. */
function measureFit(cell: HTMLElement): number {
  const texts = [...cell.querySelectorAll<HTMLElement>(FIT_TEXT)].map((el) => {
    const range = document.createRange();
    range.selectNodeContents(el);
    // A page that lays out no text (a test's) reads no width, and asks for nothing.
    const text =
      typeof range.getBoundingClientRect === 'function' ? range.getBoundingClientRect().width : 0;
    return { text, box: el.clientWidth };
  });
  return fittedWidth(cell.getBoundingClientRect().width, texts);
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

function readStored(): number {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? INSPECTOR_DEFAULT : Number(raw);
  } catch {
    return INSPECTOR_DEFAULT;
  }
}

function writeStored(width: number): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(width));
  } catch {
    // A private window or blocked storage keeps the width for this visit only.
  }
}

export function InspectorSplit({
  canvas,
  inspector,
  fitKey = null,
  measure = measureFit,
}: {
  canvas: ReactNode;
  inspector: ReactNode;
  /** The gate or step open in the inspector; null when none is. */
  fitKey?: string | null;
  /** How wide the inspector must be to show its fields whole; the page's own reading by default. */
  measure?: (cell: HTMLElement) => number;
}) {
  const [stored, setStored] = useState(readStored);
  const [row, setRow] = useState(0);
  const [lit, setLit] = useState(false);
  const [moving, setMoving] = useState(false);
  const [fit, setFit] = useState<{ key: string; width: number } | null>(null);
  // The handle took over from the fit; the next opening, or a longer field, ends that.
  const [yielded, setYielded] = useState(false);
  const [reduced] = useState(prefersReducedMotion);
  const rowRef = useRef<HTMLDivElement>(null);
  const cellRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const fitted = fit !== null && !yielded ? Math.max(stored, fit.width) : stored;
  const width = clampWidth(fitted, row);

  // Measured whenever the inspector's content changes with a gate or step open:
  // its fields change with the draft and as capability schemas arrive. Settles
  // once the fit stays put.
  useLayoutEffect(() => {
    const cell = cellRef.current;
    if (fitKey === null || !cell) return;
    const needed = measure(cell);
    if (fit && fit.key === fitKey && Math.abs(fit.width - needed) < FIT_SAME) return;
    setFit({ key: fitKey, width: needed });
    setYielded(false);
  }, [fitKey, measure, fit, inspector]);

  useEffect(() => {
    const element = rowRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      setRow(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  /** A width chosen by the viewer: kept in bounds, and kept for the next visit. */
  function commit(next: number) {
    const clamped = clampWidth(next, row);
    setYielded(true);
    setStored(clamped);
    writeStored(clamped);
  }

  /** The width the pointer asks for: from it to the row's right edge, less half the handle. */
  function fromPointer(clientX: number): number {
    const right = rowRef.current?.getBoundingClientRect().right ?? 0;
    return right - clientX - HANDLE / 2;
  }

  return (
    <div
      ref={rowRef}
      style={{
        display: 'grid',
        gridTemplateColumns: `minmax(0, 1fr) ${HANDLE}px ${width}px`,
        alignItems: 'start',
        // A fit eases; the handle follows the pointer at once.
        transition: moving || reduced ? undefined : `grid-template-columns ${EASE_MS}ms ease-out`,
      }}
    >
      {canvas}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="Inspector width"
        aria-valuemin={INSPECTOR_DEFAULT}
        aria-valuemax={widest(row)}
        aria-valuenow={width}
        tabIndex={0}
        title="Drag to resize · double-click to reset"
        onPointerDown={(e) => {
          e.preventDefault();
          e.currentTarget.setPointerCapture(e.pointerId);
          dragging.current = true;
          setMoving(true);
          // The drag starts from the width on screen, the fit's when there is one.
          setStored(width);
          setYielded(true);
        }}
        onPointerMove={(e) => {
          if (dragging.current) setStored(clampWidth(fromPointer(e.clientX), row));
        }}
        onPointerUp={(e) => {
          if (!dragging.current) return;
          dragging.current = false;
          setMoving(false);
          commit(fromPointer(e.clientX));
        }}
        onDoubleClick={() => commit(INSPECTOR_DEFAULT)}
        onKeyDown={(e) => {
          // Left widens the inspector (the handle moves left), right narrows it.
          if (e.key === 'ArrowLeft') commit(width + KEY_STEP);
          else if (e.key === 'ArrowRight') commit(width - KEY_STEP);
          else if (e.key === 'Home') commit(INSPECTOR_DEFAULT);
        }}
        onPointerEnter={() => setLit(true)}
        onPointerLeave={() => setLit(false)}
        onFocus={() => setLit(true)}
        onBlur={() => setLit(false)}
        style={{
          alignSelf: 'stretch',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'col-resize',
          touchAction: 'none',
          outline: 'none',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 4,
            height: 30,
            borderRadius: 'var(--radius-pill)',
            background: lit ? 'var(--accent)' : 'var(--border-strong)',
            transition: 'background var(--dur-micro) var(--ease-standard)',
          }}
        />
      </div>
      <div ref={cellRef} style={{ minWidth: 0 }}>
        {inspector}
      </div>
    </div>
  );
}
